<template>
    <v-card-title>
        <span class="table-header table-header--black">{{ dataGatheringInfo.title }}</span>
        <v-divider vertical class="mx-3" />
        <span class="table-header table-header--gray">{{ dataGatheringInfo.academic_period }}</span>
        <v-spacer></v-spacer>
        <v-btn
            v-if="showResumeButton"
            :disabled="dataGatheringInfo.is_busy"
            class="v-btn--resume"
            @click="resume"
        >
            {{ $t('app.resume') }}
        </v-btn>
        <v-btn
            :class="dataGatheringInfo.status_classes"
            :disabled="dataGatheringInfo.is_busy"
            @click="goToExploreBible"
        >
            {{ $t('app.explore') }}
        </v-btn>
        <v-menu v-if="showMoreOptions" left offset-y rounded="0">
            <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on">
                    <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
            </template>
            <v-list class="list">
                <v-list-item
                    v-for="(item, index) in menuItems"
                    :key="index"
                    v-if="showItem(item.action)"
                    :disabled="item.disabled(dataGathering)"
                    @click="onBibleMenuItemClick(item.action)"
                    :class="item.class ? 'list--item ' + item.class : 'list--item'"
                    link
                    dense
                >
                    <v-list-item-icon>
                        <v-icon>{{ item.icon }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title>{{ item.title }}</v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
            </v-list>
        </v-menu>

        <!-- bible edit dialog forms -->
        <edit-form-dialog
            v-model="flags.show_edit"
            :data-gathering="dataGathering"
        />

        <!-- bible replace dialog form -->
        <replace-form-dialog
            v-model="flags.show_replace"
            :data-gathering="dataGathering"
        />

        <!-- bible delete dialog form -->
        <delete-dialog
            v-model="flags.show_delete"
            :data-gathering="dataGathering"
        />
    </v-card-title>
</template>

<script>
import _ from "lodash";
import {Inertia} from "@inertiajs/inertia";
import {i18n} from "@/i18n";
import EditFormDialog from "@/Shared/DathaGatherings/Common/Forms/EditFormDialog";
import DeleteDialog from "@/Shared/DathaGatherings/Common/Forms/DeleteDialog";
import DataGatheringRepository from "@/Repositories/DataGathering/DataGatheringRepository";
import {busyStatuses, readOnlyStatuses, bibleResumeAbbr} from "@/Repositories/DataGathering/DataGatheringRepository";
import {mapActions, mapState} from 'pinia';
import {DataGatheringsStore} from "@/Store/DataGatherings/DataGatheringsStore";
import ReplaceFormDialog from "@/Shared/DathaGatherings/Common/Forms/ReplaceDialog";
import StrategySelectionMixin from "@/Shared/DathaGatherings/Common/Forms/StrategySelectionMixin";
import toastMixin from "@/Shared/toastMixin";
import DataRecordsRepository from "@/Repositories/DataGathering/DataRecordsRepository";
import {DataRecordsStore} from "@/Store/DataGatherings/DataRecordsStore";
import {PermissionsStore} from "@/Store/PermissionsStore";

const exploreButtonClassMap = [
    {
        status: ['processing'],
        classes: 'v-btn--primary--disabled mx-4'
    },
];

export default {
    name: "DataGatheringCardTitle",

    components: {ReplaceFormDialog, DeleteDialog, EditFormDialog},

    mixins: [StrategySelectionMixin, toastMixin],

    props: {
        dataGathering: {
            type: Object,
            default: DataGatheringRepository.build(),
        }
    },

    data: () => ({
        menuItems: [
            {
                title: i18n.tc('app.edit'),
                icon: "mdi-account-edit",
                disabled: dataGathering => readOnlyStatuses.inArray(dataGathering.status) || bibleResumeAbbr.inArray(dataGathering.type.abbr),
                action: 'showEdit',
            },
            {
                title: i18n.tc('app.replace'),
                icon: "mdi-file-replace",
                disabled: dataGathering => readOnlyStatuses.inArray(dataGathering.status) || bibleResumeAbbr.inArray(dataGathering.type.abbr),
                action: 'showReplace',
            },
            {
                title: i18n.tc('app.delete'),
                icon: "mdi-delete",
                class: "list--item__remove",
                disabled: dataGathering => readOnlyStatuses.inArray(dataGathering.status),
                action: 'showDelete',
            },
        ],

        flags: {
            show_edit: false,
            show_replace: false,
            show_delete: false,
        },

        data_gathering_types: {
            detail_grade_abbr: 'BIBLE_DETAIL_GRADE',
            detail_master_abbr: 'BIBLE_DETAIL_MASTER',
        },
    }),

    methods: {
        onBibleMenuItemClick(action) {
            this[action](...arguments);
        },

        showEdit() {
            this.flags.show_edit = true;
        },

        showReplace() {
            this.flags.show_replace = true;
        },

        showDelete() {
            this.flags.show_delete = true;
        },

        goToExploreBible(){
            const id = this.dataGathering.uuid;

            Inertia.get(this.getCommonRoute('explore_records', {id}));
        },

        setDataGatheringType() {
            if (this.dataGathering.type.abbr === this.data_gathering_types.detail_grade_abbr)
                return 2;
            if (this.dataGathering.type.abbr === this.data_gathering_types.detail_master_abbr)
                return 4
        },

        resume(){

            this.getRecuResume(this.dataGathering).then(data_gathering => {
                //Add new validation to check if the data gathering is already created
                if (Object.keys(data_gathering.data).length === 0){
                    this.storeResume();
                } else {
                    this.replaceResume(data_gathering.data);
                }

            }).catch(error => {
                this.toast().error(
                    this.$t('data_gatherings.generate_form.error_store_title'),
                    this.$t('data_gatherings.generate_form.error_store_message'),
                );

                console.error('data-gathering.store.error', error, _.cloneDeep(this.data_gathering));
            })
        },

        storeResume() {

            const strategyId = 3;
            const user = _.get(this.$page.props, 'auth');
            const { uuid, title, notes, academic_period, type, status } = this.dataGathering;

            this.data_gathering.setCreatedBy(user);
            this.data_gathering = {
                ...this.dataGathering,
                reference_uuid: uuid,
                title: title.replace(/DETALLADO/i, "Resumido"),
                notes,
                academic_period : {
                    ...academic_period,
                    id: academic_period.id,
                },
                type: {
                    ...type,
                    id: this.setDataGatheringType(),
                },
                strategy: { id: strategyId },
                mode: 'manual',
                options: {
                    ...this.data_gathering.options,
                    data_provider: {
                        name: 'recu',
                        uuid: uuid,
                        parent_status: status,
                    }
                }
            };
            this.store(this.data_gathering).then(data_gathering => {
                this.toast().success(
                    this.$t('data_gatherings.generate_form.success_store_title', {data_gathering: data_gathering.title}),
                    this.$t('data_gatherings.generate_form.success_store_message'),
                );
            }).catch(error => {
                this.toast().error(
                    this.$t('data_gatherings.generate_form.error_store_title'),
                    this.$t('data_gatherings.generate_form.error_store_message'),
                );

                console.error('data-gathering.store.error', error, _.cloneDeep(this.data_gathering));
            })
        },

        replaceResume(data) {
            const user = _.get(this.$page.props, 'auth');
            this.data_gathering.setReplacedBy(user);
            this.data_gathering = {
                ...data,
                options: {
                    ...data.options,
                    data_provider: {...data.options.data_provider, parent_status: this.dataGathering.status},
                    replaced_by: this.data_gathering.options.replaced_by
                }
            };

            this.replace(this.data_gathering)
                .then(data_gathering => {
                    this.toast().success(
                        this.$t('data_gatherings.replace_form.success_store_title', {data_gathering: data_gathering.title}),
                        this.$t('data_gatherings.replace_form.success_store_message'),
                    );
                })
                .catch(error => {
                    this.toast().error(
                        this.$t('data_gatherings.replace_form.error_store_title'),
                        this.$t('data_gatherings.replace_form.error_store_message'),
                    );

                    console.error('data-gathering.replace.error', error, _.cloneDeep(this.data_gathering));
                })
        },

        showItem(action) {
            switch (action) {
                case 'showEdit':
                    return this.checkPermission('DATA_SOURCES_EDIT');
                case 'showReplace':
                    return this.checkPermission('DATA_SOURCES_REPLACE');
                case 'showDelete':
                    return this.checkPermission('DATA_SOURCES_DELETE');
            }
        },

        ...mapActions(PermissionsStore, ['checkPermission']),

        ...mapActions(DataGatheringsStore, [
            'store', 'replace', 'getCommonRoute','getRecuResume',
        ]),
    },

    computed: {
        dataGatheringInfo() {
            const statusClass = exploreButtonClassMap.firstOf(map => map.status.inArray(this.dataGathering.status));

            return {
                title: _.get(this.dataGathering, 'title', '-'),
                academic_period: _.get(this.dataGathering, 'academic_period.title', '-'),
                status_classes: _.get(statusClass, 'classes', 'v-btn--primary mx-2'),
                is_closed: this.dataGathering.status === 'closed',
                is_busy: busyStatuses.inArray(this.dataGathering.status),
            };
        },

        showResumeButton() {
            if (this.checkPermission('DATA_SOURCES_RESUME')) {
                return this.dataGathering.type.abbr === this.data_gathering_types.detail_grade_abbr ||
                    this.dataGathering.type.abbr === this.data_gathering_types.detail_master_abbr
            }
            return false;
        },

        showMoreOptions() {
          return this.checkPermission('DATA_SOURCES_EDIT') || this.checkPermission('DATA_SOURCES_REPLACE') || this.checkPermission('DATA_SOURCES_DELETE');
        },
    },
}
</script>

<style scoped>
.list .v-list-item__title{
    text-transform: uppercase;
}

.v-btn--resume {
    border: 1px solid hsl(0deg, 0%, 64%);
    border-radius: 5px !important;
    margin-left: 8px;
    margin-right: 8px;
    color: hsl(0deg, 0%, 47%) !important;
    background-color: hsl(0deg, 0%, 100%) !important;
}

.v-btn--resume:hover {
    background-color: hsl(0deg, 0%, 98%) !important;
    color: hsl(217deg, 11%, 40%) !important;
    border-color: hsl(0deg, 0%, 47%);
}
.v-btn--resume:focus {
    background-color: hsl(0deg, 0%, 98%) !important;
    color: hsl(217deg, 11%, 40%) !important;
    border-color: hsl(0deg, 0%, 47%);
}
</style>
